/**
 * useHeartbeat — 장치관리자 하트비트 클라이언트 (설계 §8 / dev-steps S9)
 *
 * - 마운트 즉시 1회 + 주기(HEARTBEAT_INTERVAL_SEC) + 탭 복귀(visibilitychange) 전송
 * - 게이트: document.visibilityState==='visible' && 로그인+지점선택(store_id 보유) 상태만 (인증=staff JWT, api 인터셉터 자동 첨부)
 * - 실패는 전부 swallow(UI 무영향). 클라 실패는 텔레그램 안 함(부재=신호 → 서버 sweep §6.4 처리)
 * - device_id: 태블릿 localStorage UUID(device_token 과 별개 — 설계 §5.1/§11-3)
 * - 장치 상태: window.ZlzlBridge.getDeviceStatus()(브릿지앱) — 일반 브라우저는 {}(장치 unknown)
 * - store_id/X-Device-Token 은 api.js 인터셉터가 자동 첨부(권위=토큰)
 */
import { useEffect, useRef } from 'react';
import { checkUsbBridge, sendDeviceHeartbeat } from '../services/api';
import { buildScreenLabel } from '../constants/screenLabels';

const HEARTBEAT_INTERVAL_SEC =
  Number(process.env.REACT_APP_HEARTBEAT_INTERVAL_SEC) || 20; // 기본 20초(설계 §13: 15~30)
const DEVICE_ID_KEY = 'device_mgr_device_id';
const BUILD_VERSION_CACHE_MS = 60 * 1000;

let appBuildCache = { expiresAt: 0, value: {} };

function formatVersion(ts) {
  if (!ts) return '';
  const d = new Date(Number(ts) * 1000);
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yy}/${mm}/${dd} ${hh}:${mi}`;
}

function getOrCreateDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        window.crypto && typeof window.crypto.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return 'dev-unknown';
  }
}

async function readDeviceStatus() {
  // 브릿지앱(WebView)이면 네이티브 장치 상태, 일반 브라우저면 {}(장치 unknown)
  let status = {};
  try {
    const b = window.ZlzlBridge;
    if (b && typeof b.getDeviceStatus === 'function') {
      status = JSON.parse(b.getDeviceStatus()) || {};
    }
  } catch (e) {
    /* swallow */
  }

  try {
    const b = window.ZlzlBridge;
    if (b && typeof b.request === 'function' && !status.app_version) {
      const bridge = await checkUsbBridge();
      if (bridge?.reachable) {
        status.bridge_reachable = true;
        status.app_version = bridge.app_version || status.app_version;
        status.app_version_code = bridge.app_version_code || status.app_version_code;
        if (!status.printer) {
          status.printer = bridge.connected ? 'ok' : 'disconnected';
        }
      }
    }
  } catch (e) {
    /* swallow */
  }

  return status;
}

async function readAppBuildVersion() {
  const now = Date.now();
  if (appBuildCache.expiresAt > now) {
    return appBuildCache.value;
  }

  try {
    const res = await fetch('/version.json?t=' + now, { cache: 'no-store' });
    if (!res.ok) return appBuildCache.value;
    const data = await res.json();
    if (data?.v === null || data?.v === undefined) return appBuildCache.value;
    const raw = String(data.v);
    const label = formatVersion(raw);
    appBuildCache = {
      expiresAt: now + BUILD_VERSION_CACHE_MS,
      value: {
        app_build_version: raw,
        app_build_version_label: label,
        app_build_version_ts: Number(raw),
      },
    };
  } catch (e) {
    /* swallow */
  }
  return appBuildCache.value;
}

export function mergeHeartbeatDevice(deviceStatus, appBuild) {
  return { ...(deviceStatus || {}), ...(appBuild || {}) };
}

function currentRoute() {
  // HashRouter/BrowserRouter 모두 대응
  if (window.location.hash) return window.location.hash.replace(/^#/, '') || '/';
  return window.location.pathname || '/';
}

export default function useHeartbeat() {
  const timer = useRef(null);

  useEffect(() => {
    const tick = async () => {
      try {
        if (document.visibilityState !== 'visible') return;
        // 게이트: 로그인+지점선택(store_id) 상태만. JWT(accessToken)는 api 인터셉터가 자동 첨부.
        const storeId = localStorage.getItem('store_id');
        if (!storeId) return;
        const route = currentRoute();
        const [deviceStatus, appBuild] = await Promise.all([
          readDeviceStatus(),
          readAppBuildVersion(),
        ]);
        await sendDeviceHeartbeat({
          device_id: getOrCreateDeviceId(),
          device_type: 'tools',
          device: mergeHeartbeatDevice(deviceStatus, appBuild),
          screen: { app: 'tools', route, label: buildScreenLabel(route) },
        });
      } catch (e) {
        /* 전부 swallow — UI 무영향, 클라 실패는 텔레그램 안 함(부재=신호) */
      }
    };

    tick(); // 마운트 즉시 1회
    timer.current = setInterval(tick, HEARTBEAT_INTERVAL_SEC * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (timer.current) clearInterval(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
